import api from './api';

export interface PartnerInvitation {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  message?: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';
  invitation_token: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  sender_tenant_name?: string;
  sender_tenant_id?: string;
  invited_by_first_name?: string;
  invited_by_last_name?: string;
}

export interface ReceivedInvitation {
  id: string;
  email: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';
  invitation_token: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  sender_tenant_name: string;
  sender_tenant_id: string;
  sender_email: string;
  sender_first_name?: string;
  sender_last_name?: string;
}

export interface UserTenant {
  id: string;
  name: string;
  role: string;
  status: string;
  last_access_at?: string;
}

export interface TenantSwitchRequest {
  tenant_id: string;
}

export interface TenantSwitchResponse {
  token: string;
  tenant: {
    id: string;
    name: string;
  };
}

export const partnerService = {
  // Send invitation to partner
  async sendInvitation(invitationData: {
    email: string;
    first_name?: string;
    last_name?: string;
    message?: string;
  }): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await api.post('/partner/send-invitation', invitationData);
    return response.data;
  },

  // Get pending invitations sent by this tenant
  async getPendingInvitations(): Promise<PartnerInvitation[]> {
    const response = await api.get('/partner/pending-invitations');
    return response.data.data;
  },

  // Cancel invitation
  async cancelInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/partner/cancel-invitation/${invitationId}`);
    return response.data;
  },

  // NEW: Get received invitations for this tenant
  async getReceivedInvitations(): Promise<ReceivedInvitation[]> {
    const response = await api.get('/partner/received-invitations');
    if (!response.data.success) {
      throw new Error(response.data.message);
    }
    return response.data.data;
  },

  // NEW: Update invitation status (accept/cancel received invitation)
  async updateInvitationStatus(
    invitationId: string, 
    status: 'ACCEPTED' | 'CANCELLED'
  ): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await api.put(`/partner/update-invitation-status/${invitationId}`, { status });
    if (!response.data.success) {
      throw new Error(response.data.message);
    }
    return response.data;
  },

  // NEW: Get user's accessible tenants
  async getUserTenants(): Promise<UserTenant[]> {
    const response = await api.get('/partner/user-tenants');
    return response.data.data;
  },

  // NEW: Switch active tenant context
  async switchTenant(tenantId: string): Promise<TenantSwitchResponse> {
    const response = await api.post('/partner/switch-tenant', { tenant_id: tenantId });
    return response.data.data;
  },

  // Accept invitation (public route - no auth required)
  async acceptInvitation(acceptanceData: {
    token: string;
    password: string;
    first_name?: string;
    last_name?: string;
  }): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await api.post('/partner/accept-invitation', acceptanceData);
    return response.data;
  }
};
